import React, { useRef, useState } from "react";
import { 
	RCCAlert,
	RCCGrid, RCCHookToast, RCCServicesApi
} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import PageTitle from "src/components/PageTitle";
import { useForm } from "react-hook-form";
import LovUsersGroups from "src/components/Lov/UsersGroups";
import LovUsers from "src/components/Lov/Users";
import { standardErrorMessages } from "src/utils/errors";
import { IPermissions } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import Title from "src/components/Title";
import AppIcons from "src/components/AppIcons";
import Hierarchy from "./components/Hierarchy";
import { IRefHierarchyComponent } from "./components/Hierarchy/interface";

const Permissions: React.FC = () => { 
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { superUser, businessGroup } = useAuth();

	const [loading, setLoading] = useState(false);
	const [errorLoading, setErrorLoading] = useState(true);
	const hierarchyRef = useRef<IRefHierarchyComponent>(null);

	const { control: controlLovs, reset: resetLovs } = useForm();
	const [permissionsData, setPermissionsData] = useState<IPermissions[]>([]);
	const [selectedOption, setSelectedOption] = useState<{
		value: string;
		type: "users" | "usersGroups";
		label: string;
	} | undefined>(undefined);
	const loadPermissions = async (value: string, type: "users" | "usersGroups", label: string) => {
		try{
			hierarchyRef.current?.compress();
			hierarchyRef.current?.reset();
			setLoading(true);
			setErrorLoading(false);
			setSelectedOption({
				value,
				type,
				label
			});
			const response = await RCCServicesApi.getRequest({
				method: "Permissions/Get",
				urlParameters: {
					efa005Id: type === "users" ? value : "",
					efa004Id: type === "usersGroups" ? value : ""
				}
			});
			setLoading(false);
			setPermissionsData(response.data);
		}catch(err){
			setLoading(false);
			setErrorLoading(true);
			standardErrorMessages(err, translate("Erro ao carregar as permissões"), addToast);
		}
	};

	return (
		<RCCGrid
			container
			item
			direction="column"
		>
			{/* Header -> Results + Buttons */}
			<PageTitle
				title={translate("Permissões")}
				icon={() => <AppIcons name="configs" />}
			/>
			{superUser && businessGroup.efa001Id === 1 && (
				<RCCAlert
					title={translate("Atenção")}
					description={`${translate("Para adicionar uma nova permissao na hierarquia de menu adicione-a ao JSON na pasta src/context/Auth/permissionsMenu.ts")}.  ${translate("Conceda a permissão para o grupo de Administradores. Então será criada a ação")}.  ${translate("Após isso, na tela de itens de contrato, vincule essa nova ação criada com o item de contrato específico")}.`}
				/>
			)}

			<RCCGrid container item spacing={1}>
				<RCCGrid container item xs={12} sm={6}>
					<LovUsersGroups 
						control={controlLovs}
						label={translate("Grupo de usuários")}
						name="usersGroups"
						onChangeCapture={(option:any) => {
							resetLovs({ 
								users: {value: "", label: "" }
							});
							loadPermissions(option.value, "usersGroups", option.label);
						}}
					/>
				</RCCGrid>
				<RCCGrid container item xs={12} sm={6}>
					<LovUsers 
						control={controlLovs}
						label={translate("Usuários")}
						name="users"
						onChangeCapture={(option:any) => {
							resetLovs({  
								usersGroups: {value: "", label: "" }
							});
							loadPermissions(option.value, "users", option.label);
						}}
					/>
				</RCCGrid>
			</RCCGrid>	

			<Title color="grey" size="xs" styleInline="flex-direction: column; align-items: flex-start; gap: 4px;">
				{translate("Você pode definir as seguintes opções para cada um dos itens do menu")}:
				<p style={{marginLeft: "1rem"}}>
					{translate("Visualiza")}: {translate("Essa opção permite apenas visualizar. Não será possível alterar,inserir ou excluir")}.
				</p>
				<p style={{marginLeft: "1rem"}}>
					{translate("Cria e edita")}: {translate("Essa opção permite ao usuário criar e editar. Não será possível excluir")}.
				</p>
				<p style={{marginLeft: "1rem"}}>
					{translate("Cria, edita e exclui")}: {translate("Essa opção permite acesso completo")}.
				</p>
				<p style={{marginLeft: "1rem"}}>
					{translate("Sem acesso")}: {translate("Essa opção restringe o acesso. Impossibilitando o usuário de acessar a tela")}.
				</p>
				<p>
					<strong>{translate("Atenção")}: {translate("as opções definem o nível de acesso a tela, por exemplo, caso algum usuário tenha permissão de 'Cria, edita e exclui' na tela de mapas, mas tenha permissão de visualizar em um mapa específico, ele terá acesso completo a tela de mapas, mas conseguirá apenas visualizar o mapa em questão")}</strong>
				</p>
			</Title>	
			
			<Hierarchy 
				ref={hierarchyRef}
				permissionsData={permissionsData}
				loading={loading}
				errorLoading={errorLoading}
				selectedOption={selectedOption}
				reloadHierarchy={loadPermissions}
			/>

		</RCCGrid>
	);
};

export default Permissions;
